import { useState } from 'react';
import type { Company } from '../../../../../shared/types';
import { BaseModal } from '../../common/BaseModal';
import { ModalButtonGroup } from '../../common/ModalButtonGroup';
import { ERROR_MESSAGE_STYLE, WARNING_MESSAGE_STYLE } from '../../../styles/modalStyles';

interface ArchiveCompanyModalProps {
  company: Company;
  onClose: () => void;
  onArchive: (companyId: string) => Promise<void>;
}

export function ArchiveCompanyModal({ company, onClose, onArchive }: ArchiveCompanyModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleArchive = async () => {
    try {
      setLoading(true);
      setError('');
      await onArchive(company.id);
      onClose();
    } catch (err) {
      console.error('Error archiving company:', err);
      setError('Failed to archive company. Please try again.');
      setLoading(false);
    }
  };

  return (
    <BaseModal
      isOpen={true}
      onClose={onClose}
      title="Archive Company"
      size="small"
      closeOnEscape={!loading}
      closeOnBackdropClick={!loading}
      loading={loading}
    >
      <div style={{ marginBottom: '24px', color: '#6b7280', fontSize: '15px', lineHeight: '1.6' }}>
        <p style={{ marginBottom: '12px' }}>
          Are you sure you want to archive <strong style={{ color: '#111827' }}>{company.name}</strong>?
        </p>
        <p style={{ marginBottom: '12px' }}>
          This will move the company to the archive bin. The company and all its data will be hidden
          from the main view but can be restored later.
        </p>
        <p style={WARNING_MESSAGE_STYLE}>
          Note: To permanently delete this company, you must first archive it, then delete it from the
          archived companies view.
        </p>
      </div>

      {error && (
        <div style={ERROR_MESSAGE_STYLE}>
          {error}
        </div>
      )}

      <ModalButtonGroup
        onCancel={onClose}
        onSubmit={handleArchive}
        submitLabel="Archive Company"
        submitVariant="warning"
        isLoading={loading}
        loadingLabel="Archiving..."
      />
    </BaseModal>
  );
}
